/* ===========================================================================
   woolberry (2026-09-12) — free shipping by quantity, kept live in two places:

   1) The cart breakdown (snippets/cart-price.liquid, rendered in the drawer
      and on /cart): product row → Shipping/Delivery row → Subtotal. Liquid
      renders the rows; this fills the shipping amount and the subtotal.
        - N or more pairs in the cart → "Free shipping" / "Free delivery"
          (N = snippets/wb-free-ship-qty.liquid, mirroring the automatic
          discount "Free shipping on 2+ pairs" in admin → Discounts).
        - fewer → "Calculated at checkout" and Subtotal = items only
          (Alexander, 2026-09-26: no shipping amount in the cart). The
          per-market rate lookup below is kept but OFF (SHOW_RATE = false);
          flip it to true to show the real rate again — whatever the zone
          says TODAY, in the shopper's currency — with "Calculated at
          checkout" as the fallback if the call fails (never a wrong number).
      Broadcast re-renders the whole price block from the server on every
      cart change (theme.js swaps [data-cart-price-holder] innerHTML), so the
      rows are re-filled after each change: a MutationObserver on the holder
      and the theme's own cart events both trigger it.

   2) The PDP free-shipping bar (snippets/wb-free-shipping-bar.liquid): text
      + progress segments follow the cart count.

   RATE SOURCE — why the Storefront API and not /cart/shipping_rates.json:
   the zones are priced in each market's currency (UK £3.00, US $7.00 …).
   The old AJAX endpoint (and prepare/async_shipping_rates, and even Admin
   draftOrderCalculate) run those through the shop currency, SEK, and back,
   so the UK came out at £3.24 — measured 2026-09-12 — while checkout charges
   £3.00. The Storefront API's cartCreate → deliveryGroups → deliveryOptions
   is the same engine checkout uses and answered £3.00 GBP. It is called on
   the shop's own origin (/api/<version>/graphql.json), which Shopify allows
   from the online store WITHOUT an access token (verified the same day).
   The throwaway cart mirrors the real cart's lines (data-lines on the
   breakdown), so any future weight- or price-based rate condition is
   honoured too. SF_VERSION is a dated API version: bump it once a year
   (Shopify supports each for 12 months); an expired version simply fails
   over to "Calculated at checkout".

   Shopify needs a postcode for most countries and a state for a few before
   it will price delivery, so ADDRESS carries one valid address per market.
   Rates are per zone (whole country), so any address in the country returns
   the shopper's rate. Unknown country → no postcode → if Shopify refuses,
   "Calculated at checkout".

   Rates are cached per country+currency+lines in sessionStorage: painted
   instantly on the next open, re-checked in the background once a minute so
   an admin change still shows up within the session.
   =========================================================================== */
(function () {
  'use strict';

  var SHOW_RATE = false;      // 2026-09-26: cart says "Calculated at checkout" below the free threshold
  var SF_VERSION = '2026-07';
  var SF_API = '/api/' + SF_VERSION + '/graphql.json';
  var SF_QUERY =
    'mutation wbRate($input: CartInput!, $country: CountryCode!) @inContext(country: $country) {' +
    ' cartCreate(input: $input) {' +
    '  cart { deliveryGroups(first: 5) { nodes { deliveryOptions { title estimatedCost { amount currencyCode } } } } }' +
    '  userErrors { field message }' +
    ' }' +
    '}';

  var ADDRESS = {
    GB: { zip: 'SW1A 1AA' },
    IE: { province: 'D', zip: 'D02 X285' },
    US: { province: 'NY', zip: '10001' },
    CA: { province: 'ON', zip: 'M5V 3L9' },
    AU: { province: 'NSW', zip: '2000' },
    NZ: { zip: '6011' },
    SE: { zip: '111 22' },
    DK: { zip: '1050' },
    NO: { zip: '0150' },
    FI: { zip: '00100' },
    DE: { zip: '10115' },
    AT: { zip: '1010' },
    CH: { zip: '8001' },
    NL: { zip: '1011 AB' },
    BE: { zip: '1000' },
    FR: { zip: '75001' },
    IT: { province: 'RM', zip: '00100' },
    ES: { province: 'M', zip: '28001' },
    PT: { zip: '1000-001' },
    CZ: { zip: '110 00' },
    PL: { zip: '00-001' }
  };

  var CACHE_KEY = 'wb-ship-rates';
  var TTL = 15 * 60 * 1000;   // trust a cached rate this long
  var RECHECK = 60 * 1000;    // ...but re-ask the server after this, in the background
  var inflight = {};

  function readCache() {
    try { return JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}'); } catch (e) { return {}; }
  }
  function writeCache(c) {
    try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(c)); } catch (e) { /* private mode etc. */ }
  }
  function cached(key) {
    var c = readCache()[key];
    return c && (Date.now() - c.ts) < TTL ? c : null;
  }

  /* "56677952094473:1,56677952127241:2" (data-lines) → Storefront cart lines */
  function parseLines(spec) {
    var lines = [];
    String(spec || '').split(',').forEach(function (pair) {
      var p = pair.split(':');
      var id = parseInt(p[0], 10), qty = parseInt(p[1], 10);
      if (id > 0 && qty > 0) lines.push({ merchandiseId: 'gid://shopify/ProductVariant/' + id, quantity: qty });
    });
    return lines;
  }

  function fetchRate(country, currency, linesSpec) {
    var key = country + '|' + currency + '|' + linesSpec;
    if (inflight[key]) return inflight[key];

    var lines = parseLines(linesSpec);
    if (!lines.length) return Promise.reject(new Error('no lines'));

    var a = ADDRESS[country] || {};
    var address = { country: country };
    if (a.zip) address.zip = a.zip;
    if (a.province) address.province = a.province; // Storefront MailingAddressInput takes `province` (name or code); it has no code field

    var input = {
      lines: lines,
      buyerIdentity: { countryCode: country, deliveryAddressPreferences: [{ deliveryAddress: address }] }
    };

    inflight[key] = fetch(SF_API, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ query: SF_QUERY, variables: { input: input, country: country } })
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        var cart = res && res.data && res.data.cartCreate && res.data.cartCreate.cart;
        var groups = cart && cart.deliveryGroups && cart.deliveryGroups.nodes;
        if (!groups || !groups.length) throw new Error('no delivery options');
        // one group per shipment (one in practice); several options → the cheapest
        var total = 0, cur = null;
        groups.forEach(function (g) {
          var best = null;
          (g.deliveryOptions || []).forEach(function (o) {
            var cents = Math.round(parseFloat(o.estimatedCost && o.estimatedCost.amount) * 100);
            if (isNaN(cents)) return;
            if (best === null || cents < best) { best = cents; cur = o.estimatedCost.currencyCode; }
          });
          if (best === null) throw new Error('unparsable');
          total += best;
        });
        if (currency && cur && cur !== currency) throw new Error('currency mismatch');
        var entry = { cents: total, currency: cur || currency, ts: Date.now() };
        var c = readCache();
        c[key] = entry;
        writeCache(c);
        return entry;
      })
      .then(
        function (e) { delete inflight[key]; return e; },
        function (err) { delete inflight[key]; throw err; }
      );
    return inflight[key];
  }

  function money(cents, withCurrency) {
    var t = window.theme || {};
    var fmt = withCurrency ? t.moneyWithCurrencyFormat : t.moneyFormat;
    if (typeof t.formatMoney === 'function' && fmt) return t.formatMoney(cents, fmt);
    return (cents / 100).toFixed(2);
  }

  /* ---------- 1. cart breakdown ---------- */

  function applyTotals(root) {
    var qty = parseInt(root.getAttribute('data-free-qty'), 10) || 0;
    var count = parseInt(root.getAttribute('data-item-count'), 10) || 0;
    var items = parseInt(root.getAttribute('data-items-total'), 10) || 0;
    var country = root.getAttribute('data-country') || '';
    var currency = root.getAttribute('data-currency') || '';
    var linesSpec = root.getAttribute('data-lines') || '';
    var word = (root.getAttribute('data-word') || 'shipping').toLowerCase();
    var withCurrency = root.getAttribute('data-money') === 'with-currency';
    var shipEl = root.querySelector('[data-wb-ship]');
    var sumEl = root.querySelector('[data-wb-sum]');
    if (!shipEl || !sumEl) return;

    function setState(s) { root.setAttribute('data-wb-ship-state', s); }
    function paintFree() {
      shipEl.textContent = 'Free ' + word;
      sumEl.innerHTML = money(items, withCurrency);
      setState('free');
    }
    function paintRate(entry) {
      if (entry.cents === 0) { paintFree(); return; }
      shipEl.innerHTML = money(entry.cents, withCurrency);
      sumEl.innerHTML = money(items + entry.cents, withCurrency);
      setState('rate');
    }
    function paintUnknown() {
      shipEl.textContent = 'Calculated at checkout';
      sumEl.innerHTML = money(items, withCurrency);
      setState('unknown');
    }

    if (count === 0) { setState('empty'); return; }          // the empty state hides these rows anyway
    if (qty > 0 && count >= qty) { paintFree(); return; }     // the rule — mirrors the automatic discount
    if (!SHOW_RATE) { paintUnknown(); return; }               // no amount in the cart (2026-09-26)
    if (!country || !linesSpec) { paintUnknown(); return; }

    var key = country + '|' + currency + '|' + linesSpec;
    var hit = cached(key);
    if (hit) {
      paintRate(hit);
      if (Date.now() - hit.ts < RECHECK) return;              // fresh enough — no request
    } else {
      setState('pending');
    }

    fetchRate(country, currency, linesSpec).then(function (entry) {
      if (!root.isConnected) return;                           // the block was re-rendered meanwhile
      paintRate(entry);
    }).catch(function () {
      if (!root.isConnected || hit) return;
      paintUnknown();
    });
  }

  /* ---------- 2. PDP bar ---------- */

  function renderBar(bar, count) {
    var q = parseInt(bar.getAttribute('data-free-qty'), 10) || 0;
    if (q <= 0) return;
    var word = bar.getAttribute('data-word') || 'shipping';
    var textEl = bar.querySelector('[data-wb-fsb-text]');
    var segs = bar.querySelectorAll('.wb-fsb__seg');
    var fill = bar.querySelector('[data-wb-fsb-fill]');
    var text;

    // Alexander's copy (2026-09-13): state the rule, never "add one more".
    // Must match the Liquid in snippets/wb-free-shipping-bar.liquid.
    var qWord = { 2: 'two', 3: 'three', 4: 'four' }[q] || String(q);
    if (count >= q) {
      text = 'Free ' + word;
    } else if (q === 1) {
      text = 'Free ' + word + ' on every order';
    } else {
      text = 'Free ' + word + ' on ' + qWord + ' or more pairs';
    }

    if (textEl && textEl.textContent !== text) textEl.textContent = text;
    if (fill) fill.style.width = Math.min(100, Math.round((count * 100) / q)) + '%';
    for (var i = 0; i < segs.length; i++) segs[i].classList.toggle('is-on', i < count);
    bar.classList.toggle('wb-fsb--done', count >= q);
    bar.classList.toggle('wb-fsb--partial', count > 0 && count < q);
    bar.classList.toggle('wb-fsb--empty', count === 0);
    bar.setAttribute('data-count', String(count));
  }

  function renderBars(count) {
    if (typeof count !== 'number' || isNaN(count)) return;
    var bars = document.querySelectorAll('[data-wb-fsb]');
    for (var i = 0; i < bars.length; i++) renderBar(bars[i], count);
  }

  function syncBarsFromServer() {
    if (!document.querySelector('[data-wb-fsb]')) return;
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { renderBars(cart && cart.item_count); })
      .catch(function () {});
  }

  /* ---------- wiring ---------- */

  var totalsTimer;
  function scheduleTotals() {
    clearTimeout(totalsTimer);
    totalsTimer = setTimeout(function () {
      var roots = document.querySelectorAll('[data-wb-cart-totals]');
      for (var i = 0; i < roots.length; i++) applyTotals(roots[i]);
    }, 40);
  }

  var barTimers = [];
  function scheduleBars(e) {
    var d = e && e.detail;
    if (d && typeof d.cartCount === 'number') renderBars(d.cartCount); // theme:cart:change carries it
    barTimers.forEach(clearTimeout);
    barTimers = [400, 1500].map(function (ms) { return setTimeout(syncBarsFromServer, ms); });
  }

  function watchHolders() {
    var holders = document.querySelectorAll('[data-cart-price-holder]');
    for (var i = 0; i < holders.length; i++) {
      if (holders[i].__wbWatched) continue;
      holders[i].__wbWatched = true;
      new MutationObserver(scheduleTotals).observe(holders[i], { childList: true });
    }
  }

  function init() {
    watchHolders();
    scheduleTotals();
    var bars = document.querySelectorAll('[data-wb-fsb]');
    for (var i = 0; i < bars.length; i++) {
      renderBar(bars[i], parseInt(bars[i].getAttribute('data-count'), 10) || 0);
    }
  }

  ['theme:cart:change', 'theme:cart:added', 'theme:cart:refresh', 'cart:refresh', 'cart:updated'].forEach(function (type) {
    document.addEventListener(type, function (e) { scheduleTotals(); scheduleBars(e); });
  });
  document.addEventListener('theme:cart:add', scheduleBars); // fires on submit, before the cart answers — the delayed syncs catch it
  document.addEventListener('shopify:section:load', function () { watchHolders(); scheduleTotals(); });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
